import { get } from 'lodash';

/**
 * Aggregates assignment stats (totalHours, totalUnitCount, unitAmountGoal)
 * from multiple assignmentIds.
 *
 * When multiple tasks share the same assignment, the assignment's hours and
 * unit counts are split proportionally based on each task's estimatedHours.
 * If a task has no estimatedHours, it is treated as having 0 weight and
 * receives no share of that assignment's hours.
 *
 * For unit completion, calculates the average completion percentage across
 * assignments that have a valid unitAmountGoal.
 *
 * @param {Object} task - The timeline task object
 * @param {Object} statsMap - The assignment stats map keyed by assignmentId
 * @param {Array} allTasks - Flat array of all tasks in the timeline, used to determine sharing ratios
 * @returns {{ totalHours: number, totalUnitCount: number, unitAmountGoal: number, unitCompletionPercentage: number | null }}
 */
export const aggregateAssignmentStats = (task, statsMap, allTasks = []) => {
  const assignmentIds = task.assignmentIds || [];

  let totalHours = 0;
  let totalUnitCount = 0;
  let unitAmountGoal = 0;
  let completionSum = 0;
  let completionCount = 0;

  assignmentIds.forEach((assignmentId) => {
    const assignmentData = get(statsMap, assignmentId);
    if (!assignmentData) return;

    // Determine the proportional share for this task
    const ratio = getTaskShareRatio(task, assignmentId, allTasks);

    totalHours += (assignmentData.totalHours || 0) * ratio;

    const assignmentUnitCount = (assignmentData.totalUnitCount || 0) * ratio;
    const assignmentUnitGoal = (assignmentData.unitAmountGoal || 0) * ratio;

    totalUnitCount += assignmentUnitCount;
    unitAmountGoal += assignmentUnitGoal;

    const rawUnitCount = assignmentData.totalUnitCount || 0;
    const rawUnitGoal = assignmentData.unitAmountGoal || 0;

    if (rawUnitGoal > 0) {
      const percentage = Math.min((rawUnitCount / rawUnitGoal) * 100, 100);
      completionSum += percentage;
      completionCount += 1;
    }
  });

  const unitCompletionPercentage =
    completionCount > 0 ? completionSum / completionCount : null;

  return {
    totalHours,
    totalUnitCount,
    unitAmountGoal,
    unitCompletionPercentage,
  };
};

/**
 * Calculates the share ratio for a specific task on a specific assignment.
 *
 * The ratio is based on estimatedHours: this task's estimatedHours divided by
 * the sum of estimatedHours of all tasks that reference the same assignment.
 *
 * If no other tasks share the assignment, the ratio is 1.
 * If the task has no estimatedHours but others do, the ratio is 0.
 * If no tasks sharing the assignment have estimatedHours, the hours are split
 * equally among all sharing tasks.
 *
 * @param {Object} task - The current task
 * @param {string} assignmentId - The assignment being evaluated
 * @param {Array} allTasks - Flat list of all tasks in the timeline
 * @returns {number} A ratio between 0 and 1
 */
const getTaskShareRatio = (task, assignmentId, allTasks) => {
  if (!allTasks || allTasks.length === 0) {
    return 1;
  }

  const tasksWithAssignment = allTasks.filter((timelineTask) => {
    const taskAssignmentIds = timelineTask.assignmentIds || [];
    return taskAssignmentIds.includes(assignmentId);
  });

  if (tasksWithAssignment.length <= 1) {
    return 1;
  }

  const totalEstimatedHours = tasksWithAssignment.reduce(
    (sum, timelineTask) => sum + (Number(timelineTask.estimatedHours) || 0),
    0
  );

  const taskEstimatedHours = Number(task.estimatedHours) || 0;

  if (totalEstimatedHours === 0) {
    return 1 / tasksWithAssignment.length;
  }

  if (taskEstimatedHours === 0) {
    return 0;
  }

  return taskEstimatedHours / totalEstimatedHours;
};
